// useSliceWrapper.ts

import { Slice as RTKSlice } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { store } from '~/utils/store';

// Inter types and should not be exported
// Infer the `RootState` and `AppDispatch` types from the store itself
type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;

const useTypedDispatch: () => AppDispatch = useDispatch;

type StorePropertyNames = keyof RootState;

type StoreData<StorePropertyName extends StorePropertyNames> = {
  [K in StorePropertyName]: RootState[K];
};

type WrappedSliceMethods<Slice extends RTKSlice> = {
  [ActionName in keyof Slice['actions']]: (
    ...args: Parameters<Slice['actions'][ActionName]>
  ) => void;
};

export const useSliceWrapper = <
  Slice extends RTKSlice,
  Name extends StorePropertyNames,
>(
  slice: Slice,
  storePropertyName: Name,
): WrappedSliceMethods<Slice> & StoreData<Name> => {
  const dispatch = useTypedDispatch();
  const { actions } = slice;

  const data = useSelector<RootState, RootState[Name]>(
    (state) => state[storePropertyName],
  );
  const dataOutput = { [storePropertyName]: data } as StoreData<Name>;

  const methods = useMemo(() => Object.keys(actions).reduce((acc, k) => {
    const key = k as keyof typeof actions;
    type Method = Slice['actions'][typeof key];

    if (actions[key]) {
      return {
        ...acc,
        [key]: (...input: Parameters<Method>) => {
          dispatch(actions[key](input[0]));
        },
      };
    }
    return acc;
  }, {} as WrappedSliceMethods<Slice>), [dispatch, actions]);

  return { ...methods, ...dataOutput };
};
